
import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFetchProviders } from './provider-data/useFetchProviders';
import { useProvidersState } from './provider-data/useProvidersState';
import type { PEOProvider } from '@/types/peo.types';

/**
 * Main hook to fetch and manage PEO provider data
 */
export const useProviderData = () => {
  const { fetchProviders } = useFetchProviders();
  const [debugInfo, setDebugInfo] = useState<any>(null);

  // Wrapper for fetch function to extract diagnostic info
  const fetchProvidersWithDiagnostics = useCallback(async () => {
    try {
      const result = await fetchProviders();
      setDebugInfo(result.diagnosticInfo);
      return result.data;
    } catch (err: any) {
      // If there's diagnostic info in the error, save it
      if (err.diagnosticInfo) {
        setDebugInfo(err.diagnosticInfo);
      }
      throw err.error || err;
    }
  }, [fetchProviders]);

  // Use React Query to manage the fetching state
  const { 
    data: providers = [], 
    isLoading, 
    refetch,
    isError,
    error
  } = useQuery({
    queryKey: ['peo-providers'],
    queryFn: fetchProvidersWithDiagnostics,
    staleTime: 0, // Always refetch when needed
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 1, // Only retry once to avoid too many failed requests
  });

  // Use the providers state hook with the fetched data
  const {
    localProviders,
    setLocalProviders,
    isDeletingProvider,
    setIsDeletingProvider
  } = useProvidersState(providers);

  // Add a manual force refresh function that ensures complete refresh
  const forceRefresh = useCallback(async () => {
    console.log('Force refreshing PEO provider data');
    try {
      await refetch();
    } catch (err) {
      console.error("Error during force refresh:", err);
    }
  }, [refetch]);

  return {
    providers: localProviders,
    setLocalProviders,
    originalProviders: providers,
    isLoading,
    isError,
    error,
    refetch: forceRefresh,
    isDeletingProvider,
    setIsDeletingProvider,
    debugInfo
  };
};
